import { customElement } from "lit/decorators.js";
import { PropertyValues } from "lit";
import { SugarElement } from "../../viewport/SugarCanvas";
import { Engine, Entity } from "../../../engine/Engine";
import { AccumulatingGTAOPass } from "../../../engine/postprocesses/AccumulatingGTAOPass";
import { GTAOPass } from "../../../engine/postprocesses/GTAOPass";
import { CameraTracker } from "../../../engine/camera/CameraTracker";
import { logger } from "../../../util/Logger";
import { AoTslEffect } from "./tsl/TslEffects";

/**
 * GTAO post-process that temporally accumulates ambient occlusion across
 * frames (classic WebGL). On WebGPU registers AoTslEffect when SSGI is off.
 *
 * Attributes:
 *   - `max-samples` (number, default 256): frames to accumulate before the
 *     pass stops requesting further redraws.
 *   - `denoise` (boolean, default false): when set, Poisson-denoises the
 *     accumulated AO before blending onto the scene.
 */
class GTAOAccumulatePostProcessEntity extends Entity {
  private _gtaoPass?: AccumulatingGTAOPass;
  private _tslEffect?: AoTslEffect;
  /**
   * Host apply owns enable. Default off so GTAO/SSGI stay unselected until
   * settings push an explicit mode.
   */
  private _settingsEnabled = false;
  private _maxSamples = 8;
  private _denoise = false;
  /** Poisson denoise tunables (WebGL accumulating path). */
  private _pd = {
    lumaPhi: 10,
    depthPhi: 2,
    normalPhi: 1,
    radius: 2,
    radiusExponent: 1,
    rings: 2,
    samples: 16,
  };
  /** Kullanıcının verdiği değerler; biri undefined ise diğeri aspect ratio'dan türetilir. */
  private _resolutionWidth?: number;
  private _resolutionHeight?: number;

  /** View-space AO params — default matches SettingsMenu GTAO presets. */
  private _ao = {
    radius: 0.25,
    samples: 32,
    scale: 1,
    thickness: 1,
    distanceExponent: 2.5,
    distanceFallOff: 1,
    resolutionScale: 1,
    intensity: 1,
    useTemporalFiltering: false,
  };
  /**
   * İkisi de verilmediğinde kullanılacak temel çözünürlük cihaza göre seçilir:
   * desktop → 1024, düşük işlemcili android → 512 (diğer durumlar 1024).
   */
  private static _defaultResolution(): number {
    if (typeof navigator === "undefined") return 1024;
    const ua = navigator.userAgent || "";
    const isAndroid = /Android/i.test(ua);
    if (isAndroid) {
      const cores = navigator.hardwareConcurrency ?? 4;
      const mem = (navigator as Navigator & { deviceMemory?: number })
        .deviceMemory ?? 4;
      if (cores <= 4 || mem <= 4) return 512;
    }
    return 1024;
  }
  private _tracker?: CameraTracker;
  /** Engine listener'ları/tracker yalnızca bir kez bağlansın (pass yeniden kurulduğunda çift kayıt olmasın). */
  private _listenersBound = false;
  /** Last logged frame progress, so we only print every ~16 frames. */
  private _lastLoggedFrame = -1;

  private _pauseAccumulation = (reason: string): void => {
    if (!this._gtaoPass) return;
    this._gtaoPass.reset();
    this._gtaoPass.setAccumulate(false);
    this.engine?.dispatchCustomEvent("request-update");
  };

  private _resumeAccumulation = (reason: string): void => {
    if (!this._gtaoPass) return;

    this._gtaoPass.reset();
    this._gtaoPass.setAccumulate(true);
    this.engine?.dispatchCustomEvent("request-update");
  };

  private _interactionStartHandler = () => this._pauseAccumulation("interaction-start");
  private _interactionEndHandler = () => this._resumeAccumulation("interaction-end");

  private _syncGtaoSceneAndCamera(): void {
    const eng = this.engine;
    if (!this._gtaoPass || !eng?.activeCamera) return;
    this._gtaoPass.scene = eng.getSceneForWebGLRender();
    this._gtaoPass.camera = eng.activeCamera.instance;
  }

  private _onWebglRenderSceneChanged = () => {
    this._syncGtaoSceneAndCamera();
    this._gtaoPass?.reset();
  };

  /** Sahne içeriği (mesh/material/transform) — Engine SceneMutationObserver → scene-mutated */
  private _onSceneChanged = () => {
    this._syncGtaoSceneAndCamera();
    this._gtaoPass?.reset();
    //this.engine?.dispatchCustomEvent("request-update");
  };

  private _onUpdateCamera = (_e?: unknown) => {
    this._syncGtaoSceneAndCamera();
    this._gtaoPass?.reset();
  };

  private _onEngineResize = () => {
    // Yalnızca tek kenar verildiyse diğeri aspect ratio'ya bağlı; resize'da
    // yeniden hesaplayıp pass'i gerekirse yeniden kuruyoruz.
    const onlyOneGiven =
      (this._resolutionWidth == null) !== (this._resolutionHeight == null);
    if (onlyOneGiven && this._gtaoPass) {
      const { width, height } = this._effectiveResolution();
      if (width !== this._gtaoPass.width || height !== this._gtaoPass.height) {
        this._rebuildPass();
        return;
      }
    }
    this._gtaoPass?.reset();
  };

  private _onContinueAccumulation = () => {
    // Engine zaten sürekli rAF ile WebGL çizer; request-update yalnızca CSS2D
    // render'ını tetikler ve DOM'u gereksiz yeniler (GTAO birikim flicker'ı).
  };

  /**
   * Efektif render hedefi boyutu. Yalnızca bir kenar verildiyse diğeri
   * engine'in aspect ratio'suna göre hesaplanır; hiçbiri verilmediyse kare
   * varsayılan kullanılır.
   */
  private _effectiveResolution(): { width: number; height: number } {
    const base = GTAOAccumulatePostProcessEntity._defaultResolution();
    const w = this._resolutionWidth;
    const h = this._resolutionHeight;

    if (w != null && h != null) return { width: w, height: h };
    if (w == null && h == null) return { width: base, height: base };

    const ew = this.engine?.width ?? 0;
    const eh = this.engine?.height ?? 0;
    const aspect = ew > 0 && eh > 0 ? ew / eh : 1;

    if (w != null) {
      return { width: w, height: Math.max(1, Math.round(w / aspect)) };
    }
    return { width: Math.max(1, Math.round(h! * aspect)), height: h! };
  }

  private _addGTAOPass(): void {
    if (!this._settingsEnabled) return;
    const eng = this.engine;
    if (!eng) return;

    if (eng.renderer.isWebGPURenderer) {
      if (this._tslEffect) return;
      this._tslEffect = new AoTslEffect({ ...this._ao });
      eng.addEffect(this._tslEffect);
      return;
    }

    if (this._gtaoPass) return;
    if (!eng.activeCamera || !eng.activeScene) return;

    const aoParameters = {
      radius: this._ao.radius,
      distanceExponent: this._ao.distanceExponent,
      thickness: this._ao.thickness,
      scale: this._ao.scale,
      samples: this._ao.samples,
      distanceFallOff: this._ao.distanceFallOff,
      screenSpaceRadius: true,
      output: GTAOPass.OUTPUT.AO,
    };
    const pdParameters = {
      lumaPhi: this._pd.lumaPhi,
      depthPhi: this._pd.depthPhi,
      normalPhi: this._pd.normalPhi,
      radius: this._pd.radius,
      radiusExponent: this._pd.radiusExponent,
      rings: this._pd.rings,
      samples: this._pd.samples,
      active: this._denoise,
    };

    const { width, height } = this._effectiveResolution();
    this._gtaoPass = new AccumulatingGTAOPass(
      eng.getSceneForWebGLRender(),
      eng.activeCamera.instance,
      width,
      height,
      { maxSamples: this._maxSamples, denoise: this._denoise },
    );
    this._gtaoPass.updateGtaoMaterial(aoParameters);
    this._gtaoPass.updatePdMaterial(pdParameters);
    this._gtaoPass.blendIntensity = this._ao.intensity;
    this._gtaoPass._onContinueAccumulation = this._onContinueAccumulation;

    eng.removePass(this._gtaoPass);
    eng.addPass(this._gtaoPass);

    if (this._listenersBound) return;
    this._listenersBound = true;

    eng.addEventListener("update-camera", this._onUpdateCamera);
    eng.addEventListener(
      "webgl-render-scene-changed",
      this._onWebglRenderSceneChanged,
    );
    eng.addEventListener(
      "interaction-start",
      this._interactionStartHandler,
    );
    eng.addEventListener(
      "interaction-end",
      this._interactionEndHandler,
    );
    eng.addEventListener("engine-resize", this._onEngineResize);
    eng.addEventListener("scene-mutated", this._onSceneChanged);

    // High-level camera motion signal. The tracker consolidates camera-start
    // / camera-change / camera-end into clean motion transitions, filtering
    // out SmoothOrbitController's per-frame `camera-change` noise. During
    // motion we additionally reset each tick so nothing accumulates while
    // the view is still drifting.
    this._tracker = new CameraTracker({
      engine: eng,
      label: "GTAO",
      onMotionStart: () => this._pauseAccumulation("motion-start"),
      onMotionChange: () => this._gtaoPass?.reset(),
      onMotionEnd: () => this._resumeAccumulation("motion-end"),
    });
  }

  public setMaxSamples(n: number): void {
    if (this._maxSamples === n) {
      this._gtaoPass?.setMaxSamples(n);
      return;
    }
    this._maxSamples = n;
    this._gtaoPass?.setMaxSamples(n);
    this._lastLoggedFrame = -1;
    this.engine?.dispatchCustomEvent("request-update");
  }

  public setEnabled(enabled: boolean): void {
    this._settingsEnabled = enabled;
    const eng = this.engine;
    // Flag stored; onAdd attaches when engine/camera become ready.
    if (!eng) return;

    if (eng.renderer.isWebGPURenderer) {
      if (!enabled) {
        if (this._tslEffect) {
          eng.removeEffect(this._tslEffect);
          this._tslEffect = undefined;
        }
      } else {
        this._addGTAOPass();
      }
      eng.dispatchCustomEvent("request-update");
      return;
    }
    if (!enabled) {
      eng.removePass(this._gtaoPass);
      this._gtaoPass?.dispose();
      this._gtaoPass = undefined;
    } else {
      this._addGTAOPass();
    }
    eng.dispatchCustomEvent("request-update");
  }

  public setGtaoParameters(
    radius: number,
    samples: number,
    intensity: number,
  ): void {
    this._ao.radius = radius;
    this._ao.samples = samples;
    this._ao.intensity = intensity;
    if (this.engine?.renderer.isWebGPURenderer) {
      this._tslEffect?.setParameters({
        radius,
        samples,
        intensity,
      });
      this.engine.dispatchCustomEvent("request-update");
      return;
    }
    if (!this._gtaoPass) return;
    this._gtaoPass.updateGtaoMaterial({ radius, samples });
    this._gtaoPass.blendIntensity = intensity;
    this._gtaoPass.reset();
    this.engine?.dispatchCustomEvent("request-update");
  }

  /** Full AO param set from the Lit element attributes. */
  public setAoFromElement(params: Partial<typeof this._ao>): void {
    Object.assign(this._ao, params);
    if (this._tslEffect) {
      this._tslEffect.setParameters({ ...this._ao });
      this.engine?.dispatchCustomEvent("request-update");
      return;
    }
    if (this.engine?.renderer.isWebGPURenderer) return;
    if (this._gtaoPass) {
      this._gtaoPass.updateGtaoMaterial({
        radius: this._ao.radius,
        samples: this._ao.samples,
        scale: this._ao.scale,
        thickness: this._ao.thickness,
        distanceExponent: this._ao.distanceExponent,
        distanceFallOff: this._ao.distanceFallOff,
      });
      this._gtaoPass.blendIntensity = this._ao.intensity;
      this._gtaoPass.reset();
      this.engine?.dispatchCustomEvent("request-update");
    }
  }

  public setDenoise(enabled: boolean): void {
    if (this._denoise === enabled) {
      this._gtaoPass?.setDenoise(enabled);
      return;
    }
    this._denoise = enabled;
    this._gtaoPass?.setDenoise(enabled);
    this.engine?.dispatchCustomEvent("request-update");
  }

  /** Poisson denoise params from settings / Lit attributes. */
  public setPdFromElement(
    params: Partial<typeof this._pd> & { denoise?: boolean },
  ): void {
    const { denoise, ...pd } = params;
    Object.assign(this._pd, pd);
    if (denoise !== undefined) this._denoise = denoise;
    if (!this._gtaoPass) return;
    this._gtaoPass.updatePdMaterial({
      lumaPhi: this._pd.lumaPhi,
      depthPhi: this._pd.depthPhi,
      normalPhi: this._pd.normalPhi,
      radius: this._pd.radius,
      radiusExponent: this._pd.radiusExponent,
      rings: this._pd.rings,
      samples: this._pd.samples,
      active: this._denoise,
    });
    this.engine?.dispatchCustomEvent("request-update");
  }

  public setResolution(width?: number, height?: number): void {
    if (width === this._resolutionWidth && height === this._resolutionHeight) {
      return;
    }
    this._resolutionWidth = width;
    this._resolutionHeight = height;
    this._rebuildPass();
    this.engine?.dispatchCustomEvent("request-update");
  }

  /**
   * AccumulatingGTAOPass'in render target'ları yapıcıda kuruluyor; çözünürlük
   * değişince pass'i yeniden oluşturmak en güvenlisi. Listener'lar
   * `_listenersBound` guard'ı sayesinde tekrar bağlanmaz.
   */
  private _rebuildPass(): void {
    if (!this._gtaoPass) return;
    this.engine?.removePass(this._gtaoPass);
    this._gtaoPass.dispose();
    this._gtaoPass = undefined;
    this._addGTAOPass();
  }

  onAdd(_engine: Engine): void {
    const tryAdd = () => {
      if (!this.engine?.activeCamera || !this.engine.activeScene) {
        requestAnimationFrame(tryAdd);
        return;
      }
      // Only attach when host apply (or setEnabled) has enabled us.
      if (this._settingsEnabled) this._addGTAOPass();
    };
    tryAdd();
  }

  onRemove(engine: Engine): void {
    this._tracker?.dispose();
    this._tracker = undefined;

    if (this._tslEffect) {
      engine.removeEffect(this._tslEffect);
      this._tslEffect = undefined;
    }
    engine.removePass(this._gtaoPass);
    engine.removeEventListener("update-camera", this._onUpdateCamera);
    engine.removeEventListener(
      "webgl-render-scene-changed",
      this._onWebglRenderSceneChanged,
    );
    engine.removeEventListener(
      "interaction-start",
      this._interactionStartHandler,
    );
    engine.removeEventListener("interaction-end", this._interactionEndHandler);
    engine.removeEventListener("engine-resize", this._onEngineResize);
    engine.removeEventListener("scene-mutated", this._onSceneChanged);
    this._listenersBound = false;
    this._gtaoPass?.dispose();
    this._gtaoPass = undefined;
  }
}

@customElement("gtao-accumulate-postprocess")
export class GTAOAccumulatePostProcessElement extends SugarElement {
  static override properties = {
    ...SugarElement.properties,
    maxSamples: { type: Number, attribute: "max-samples" },
    denoise: { type: Boolean },
    resolutionWidth: { type: Number, attribute: "resolution-width" },
    resolutionHeight: { type: Number, attribute: "resolution-height" },
    aoRadius: { type: Number, attribute: "ao-radius" },
    aoSamples: { type: Number, attribute: "ao-samples" },
    aoScale: { type: Number, attribute: "ao-scale" },
    aoThickness: { type: Number, attribute: "ao-thickness" },
    aoDistanceExponent: { type: Number, attribute: "ao-distance-exponent" },
    aoDistanceFallOff: { type: Number, attribute: "ao-distance-falloff" },
    aoResolutionScale: { type: Number, attribute: "ao-resolution-scale" },
    aoIntensity: { type: Number, attribute: "ao-intensity" },
  };

  maxSamples = 256;
  denoise = false;
  resolutionWidth?: number;
  resolutionHeight?: number;

  /** View-space meters — SettingsMenu GTAO defaults. */
  aoRadius = 0.25;
  aoSamples = 32;
  aoScale = 1;
  aoThickness = 1;
  aoDistanceExponent = 2.5;
  aoDistanceFallOff = 1;
  aoResolutionScale = 1;
  aoIntensity = 1;

  createEntity(): Entity | undefined {
    const ent = new GTAOAccumulatePostProcessEntity();
    ent.setAoFromElement({
      radius: this.aoRadius,
      samples: this.aoSamples,
      scale: this.aoScale,
      thickness: this.aoThickness,
      distanceExponent: this.aoDistanceExponent,
      distanceFallOff: this.aoDistanceFallOff,
      resolutionScale: this.aoResolutionScale,
      intensity: this.aoIntensity,
    });
    return ent;
  }

  private _pushAoToEntity(): void {
    const ent = this.entity as GTAOAccumulatePostProcessEntity | undefined;
    ent?.setAoFromElement({
      radius: this.aoRadius,
      samples: this.aoSamples,
      scale: this.aoScale,
      thickness: this.aoThickness,
      distanceExponent: this.aoDistanceExponent,
      distanceFallOff: this.aoDistanceFallOff,
      resolutionScale: this.aoResolutionScale,
      intensity: this.aoIntensity,
    });
  }

  applySettings(settings: {
    enabled: boolean;
    intensity: number;
    radius: number;
    samples: number;
    scale: number;
    thickness: number;
    distanceExponent: number;
    distanceFallOff: number;
    resolutionScale: number;
    useTemporalFiltering: boolean;
    denoise: boolean;
    lumaPhi: number;
    depthPhi: number;
    normalPhi: number;
    pdRadius: number;
  }): void {
    this.aoRadius = settings.radius;
    this.aoSamples = settings.samples;
    this.aoScale = settings.scale;
    this.aoThickness = settings.thickness;
    this.aoDistanceExponent = settings.distanceExponent;
    this.aoDistanceFallOff = settings.distanceFallOff;
    this.aoResolutionScale = settings.resolutionScale;
    this.aoIntensity = settings.intensity;
    this.denoise = settings.denoise;
    const ent = this.entity as GTAOAccumulatePostProcessEntity | undefined;
    if (!ent) return;
    ent.setEnabled(settings.enabled);
    ent.setAoFromElement({
      radius: settings.radius,
      samples: settings.samples,
      scale: settings.scale,
      thickness: settings.thickness,
      distanceExponent: settings.distanceExponent,
      distanceFallOff: settings.distanceFallOff,
      resolutionScale: settings.resolutionScale,
      intensity: settings.intensity,
      useTemporalFiltering: settings.useTemporalFiltering,
    });
    ent.setPdFromElement({
      denoise: settings.denoise,
      lumaPhi: settings.lumaPhi,
      depthPhi: settings.depthPhi,
      normalPhi: settings.normalPhi,
      radius: settings.pdRadius,
    });
  }

  override firstUpdated(e: unknown): void {
    super.firstUpdated(e as never);
    const ent = this.entity as GTAOAccumulatePostProcessEntity | undefined;
    if (!ent) return;
    ent.setResolution(this.resolutionWidth, this.resolutionHeight);
    ent.setMaxSamples(this.maxSamples);
    ent.setDenoise(this.denoise);
    this._pushAoToEntity();
  }

  protected override update(changedProperties: PropertyValues): void {
    super.update(changedProperties);
    const ent = this.entity as GTAOAccumulatePostProcessEntity | undefined;
    if (!ent) return;
    if (
      changedProperties.has("resolutionWidth") ||
      changedProperties.has("resolutionHeight")
    ) {
      ent.setResolution(this.resolutionWidth, this.resolutionHeight);
    }
    if (changedProperties.has("maxSamples")) {
      ent.setMaxSamples(this.maxSamples);
    }
    if (changedProperties.has("denoise")) {
      ent.setDenoise(this.denoise);
    }
    if (
      changedProperties.has("aoRadius") ||
      changedProperties.has("aoSamples") ||
      changedProperties.has("aoScale") ||
      changedProperties.has("aoThickness") ||
      changedProperties.has("aoDistanceExponent") ||
      changedProperties.has("aoDistanceFallOff") ||
      changedProperties.has("aoResolutionScale") ||
      changedProperties.has("aoIntensity")
    ) {
      this._pushAoToEntity();
    }
  }
}
